import type { PageKey } from "../../common/auth";
import type { AdminUserRow } from "../../common/requests/users/endpoints";
import type { Tone } from "../../ui";

export type UserSegment = "total" | "sellers" | "buyers" | "admins";

export const PERMISSION_GROUPS: {
  label: string;
  parent?: PageKey;
  parentLabel?: string;
  children: { key: PageKey; label: string }[];
}[] = [
  {
    label: "Sales",
    parent: "sales",
    parentLabel: "Open Sales page",
    children: [{ key: "sales_view_user_identity", label: "Show seller/buyer identities" }],
  },
  {
    label: "Dashboard access",
    children: [
      { key: "withdrawals", label: "Active withdrawals" },
      { key: "notifications", label: "Notifications" },
    ],
  },
  {
    label: "Seller applications",
    parent: "seller_creation",
    parentLabel: "Open Seller applications",
    children: [{ key: "seller_creation_create", label: "Approve applications" }],
  },
  {
    label: "Users & access",
    children: [{ key: "registered_users", label: "View Users & Sellers page" }],
  },
];

export const groupKeys = (group: (typeof PERMISSION_GROUPS)[number]): PageKey[] =>
  [group.parent, ...group.children.map((c) => c.key)].filter(Boolean) as PageKey[];

/** Ticks or unticks a whole group (all on unless every key is already on). */
export function toggleGroupPermissions(current: PageKey[], keys: PageKey[]): PageKey[] {
  const allSelected = keys.every((k) => current.includes(k));
  if (allSelected) return current.filter((p) => !keys.includes(p));
  return Array.from(new Set([...current, ...keys]));
}

/** A user's marketplace kind. A turned-off seller keeps seller_info (tariff),
 * which is how we tell them apart from someone who never sold. */
export function userKind(u: Pick<AdminUserRow, "is_seller" | "tariff_rate">): { label: string; tone: Tone } {
  if (u.is_seller === true) return { label: "Seller", tone: "success" };
  if (u.tariff_rate !== null && u.tariff_rate !== undefined) return { label: "Seller off", tone: "danger" };
  return { label: "Buyer", tone: "neutral" };
}

export function roleTone(role: string | null | undefined): Tone {
  if (role === "superadmin") return "ink";
  if (role === "admin" || role === "member") return "info";
  return "outline";
}

export const roleLabel = (role: string | null | undefined) => (role ? role.charAt(0).toUpperCase() + role.slice(1) : "No access");

export const sameId = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.trim() === b.trim();

export const findUser = (users: AdminUserRow[], id: string | null | undefined) =>
  id ? users.find((u) => sameId(u.user_id, id)) ?? null : null;

export const rowId = (u: AdminUserRow) => (u.user_id || u.email || "").trim();

const ROLE_RANK: Record<string, number> = { superadmin: 0, admin: 1, member: 2 };

/** The segment's rows, searched by name, email or id. Sellers list admins
 * first, then newest; other segments keep the server order (newest first). */
export function filterUsers(users: AdminUserRow[], segment: UserSegment, query: string): AdminUserRow[] {
  let rows = users.filter((u) => {
    if (segment === "sellers") return u.is_seller === true;
    if (segment === "buyers") return u.is_seller !== true;
    if (segment === "admins") return !!u.admin_user_id;
    return true;
  });
  if (segment === "sellers" || segment === "admins") {
    rows = [...rows].sort((a, b) => {
      const ra = ROLE_RANK[a.role || ""] ?? 3;
      const rb = ROLE_RANK[b.role || ""] ?? 3;
      if (ra !== rb) return ra - rb;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((u) => `${u.display_name ?? ""} ${u.email ?? ""} ${u.user_id ?? ""}`.toLowerCase().includes(q));
}

/** "10.5" (percent) -> 0.105, rounded to 4 places; an error string when invalid. */
export function parseTariffPercent(draft: string): { rate: number } | { error: string } {
  const text = draft.trim();
  const parsed = Number(text);
  if (text === "" || Number.isNaN(parsed)) return { error: "Tariff rate must be a number." };
  const percent = Math.round(parsed * 100) / 100;
  if (percent < 0 || percent > 100) return { error: "Tariff rate must be between 0 and 100 (%)." };
  return { rate: Math.round((percent / 100) * 10000) / 10000 };
}

export const sameTariff = (a: number | null | undefined, b: number) => Math.round(Number(a ?? 0) * 10000) === Math.round(b * 10000);

/** Strips spaces, brackets and dashes; "" means no number. */
export const normalizeWhatsapp = (draft: string) => draft.replace(/[\s()-]/g, "");

export const tariffPercentText = (rate: number | null | undefined) => `${(Number(rate ?? 0) * 100).toFixed(1)}%`;

export const tariffDraft = (rate: number | null | undefined) => (Number(rate ?? 0) * 100).toFixed(2);
